/**
 * The pure parts of a text's edit mode (handoff §15): where the gap underlines sit
 * while the body is being typed, what the last edit did to the gaps (the change
 * strip), what the save sheet lists, and the one PATCH body that saves everything.
 *
 * Nothing here searches the body for a gap (ADR-115 Decision 4): occurrences are the
 * server's, from the preview. Between two previews they are only shifted to follow
 * the typing.
 *
 * Card marks (ADR-115 Amendment 3) are spans of the draft body the learner marked as
 * Card in the picker. They make cards when the edit is saved, in the deck the learner
 * chooses (Amendment 4), preselected here; removing one only removes the mark.
 */
import type {
  GapAlternativesEdit, GapLabelEdit, TextAnalysis, TextDetail, TextPreviewGap, TextUpdatePayload,
} from '../types'
import { EMPTY_MARKS, hasCard, saveCardMarks, wordKey, type Marks, type Phrase, type WordGap } from './textMarks'

export type Span = [number, number]

/**
 * *spans* (indices into *from*) mapped onto *to*, the body after a typed edit. What
 * differs is found as the common prefix and suffix; a span wholly before the change
 * stays, one wholly after it moves by the length change, and one that touches the
 * changed region is dropped until the next preview draws it again.
 */
export function shiftSpans(spans: Span[], from: string, to: string): Span[] {
  if (from === to) return spans
  const limit = Math.min(from.length, to.length)
  let prefix = 0
  while (prefix < limit && from[prefix] === to[prefix]) prefix++
  let suffix = 0
  while (suffix < limit - prefix && from[from.length - 1 - suffix] === to[to.length - 1 - suffix]) suffix++
  const changedEnd = from.length - suffix
  const delta = to.length - from.length
  const out: Span[] = []
  for (const [start, end] of spans) {
    if (end <= prefix) out.push([start, end])
    else if (start >= changedEnd) out.push([start + delta, end + delta])
  }
  return out
}

/** What the change strip says about the last applied preview (handoff §15.2). */
export type Strip =
  | { kind: 'removed'; form: string }
  | { kind: 'phrase'; form: string }
  | { kind: 'grew'; form: string; count: number }
  | { kind: 'empty' }

const isLive = (g: TextPreviewGap) => g.status !== 'removed'

/**
 * The strip for the move from *previous* to *current*, or null when that edit changed
 * no gap. Only the latest change shows, in reading order of the first one found: a
 * gap that lost its last occurrence (a phrase named as a phrase), else one that gained
 * a place. A gap the learner removed by hand (its form is no longer in *wanted*) is
 * not the text's doing, so it is not reported.
 */
export function deriveStrip(
  previous: TextPreviewGap[] | null,
  current: TextPreviewGap[],
  wanted: string[],
): Strip | null {
  if (!previous) return null
  const before = new Map(previous.map((g) => [g.form, g]))
  for (const g of current) {
    const was = before.get(g.form)
    if (g.status === 'removed' && was && isLive(was) && wanted.includes(g.form)) {
      return g.form.includes(' ') ? { kind: 'phrase', form: g.form } : { kind: 'removed', form: g.form }
    }
  }
  for (const g of current) {
    const was = before.get(g.form)
    if (isLive(g) && was && isLive(was) && g.occurrence_count > was.occurrence_count) {
      return { kind: 'grew', form: g.form, count: g.occurrence_count }
    }
  }
  return null
}

/** A span of the draft body marked as Card, with its text as written (what the screen names). */
export interface EditCardMark {
  start: number
  end: number
  text: string
}

/** The learner's answer to «where do the cards go» (Amendment 4): any own deck, or the deck this edit makes. */
export type CardDeckChoice =
  | { kind: 'new_deck' }
  | { kind: 'existing_deck'; id: string; title: string }

/**
 * The deck this edit makes (Amendment 4), described before the save makes it.
 * `title` null means the text's title as the draft has it. `linked` is true when it
 * came from «Привязать колоду» → «Новая колода»: then it is made whatever deck the
 * cards go to; otherwise only while it is the cards' deck.
 */
export interface DraftNewDeck {
  title: string | null
  isActive: boolean
  linked: boolean
}

/** Everything the learner has changed so far, held apart from the saved text until «Сохранить». */
export interface EditDraft {
  title: string
  body: string
  /** The wanted gap set, forms as the server last stored or matched them. */
  forms: string[]
  deckIds: string[]
  /** Linked decks' titles, including the ones just linked: the save sheet names them. */
  deckTitles: Record<string, string>
  dropWaiting: string[]
  /** Per gap form. */
  labelEdits: Record<string, GapLabelEdit>
  /** Per gap form: answers to add, and ids of saved answers to remove. */
  alternativeEdits: Record<string, GapAlternativesEdit>
  /** Card marks, in text order. */
  cardMarks: EditCardMark[]
  /** The learner's choice of deck for the cards; null keeps the default (`deckChoice`). */
  cardDeck: CardDeckChoice | null
  newDeck: DraftNewDeck | null
}

export function draftFrom(text: TextDetail): EditDraft {
  return {
    title: text.title,
    body: text.body,
    forms: text.gaps.map((g) => g.form),
    deckIds: text.deck_ids,
    deckTitles: Object.fromEntries(text.decks.map((d) => [d.id, d.title])),
    dropWaiting: [],
    labelEdits: {},
    alternativeEdits: {},
    cardMarks: [],
    cardDeck: null,
    newDeck: null,
  }
}

const WORD_CHAR = /[\p{L}\p{N}]/u

/**
 * *marks* (spans of *from*) carried onto *to* after a typed edit. A mark the edit
 * touched, or whose word now runs into a neighbouring letter, is dropped: it is no
 * longer the word the learner marked. Nothing is searched for; a mark only moves.
 */
export function shiftCardMarks(marks: EditCardMark[], from: string, to: string): EditCardMark[] {
  if (from === to) return marks
  const out: EditCardMark[] = []
  for (const m of marks) {
    const shifted = shiftSpans([[m.start, m.end]], from, to)[0]
    if (!shifted) continue
    const [start, end] = shifted
    if (to.slice(start, end) !== m.text) continue
    if (WORD_CHAR.test(to[start - 1] ?? '') || WORD_CHAR.test(to[end] ?? '')) continue
    out.push({ start, end, text: m.text })
  }
  return out
}

/**
 * Where an edit's cards go (ADR-115 Amendment 4 Decision 1): the learner's choice,
 * always shown. Until they choose, the default is the text's one deck when the draft
 * leaves exactly one link (a new deck from «Привязать колоду» counts); nothing with
 * several, so the save waits; a new deck named after the text with none, as in step 3.
 * - **existing:** an own deck; `linked` when it is among the draft's links already,
 *   otherwise the save links it;
 * - **new:** the deck the save makes and links;
 * - **unchosen:** several links and no choice yet.
 */
export type ReceivingDeck =
  | { kind: 'existing'; id: string; title: string; linked: boolean }
  | { kind: 'new'; title: string; isActive: boolean }
  | { kind: 'unchosen' }

/** The name the new deck gets: the learner's, else the text's title as the draft has it. */
export const newDeckTitle = (d: EditDraft) => d.newDeck?.title?.trim() || d.title.trim()

const newDeckOf = (d: EditDraft): ReceivingDeck => ({ kind: 'new', title: newDeckTitle(d), isActive: d.newDeck?.isActive ?? true })
const existingOf = (d: EditDraft, id: string, title?: string): ReceivingDeck => ({
  kind: 'existing', id, title: title ?? d.deckTitles[id] ?? '', linked: d.deckIds.includes(id),
})

/** The deck the cards would go to, chosen or by default, whether or not there are card marks yet (the picker asks before). */
export function deckChoice(d: EditDraft): ReceivingDeck {
  const c = d.cardDeck
  if (c?.kind === 'existing_deck') return existingOf(d, c.id, c.title)
  if (c?.kind === 'new_deck') return newDeckOf(d)
  const links = d.deckIds.length + (d.newDeck?.linked ? 1 : 0)
  if (links === 0) return newDeckOf(d)
  if (links > 1) return { kind: 'unchosen' }
  return d.deckIds.length === 1 ? existingOf(d, d.deckIds[0]) : newDeckOf(d)
}

/** The receiving deck of the draft's card marks; null when there are none. */
export const receivingDeck = (d: EditDraft): ReceivingDeck | null => (d.cardMarks.length > 0 ? deckChoice(d) : null)

/** The existing deck that receives the cards, once there is one: what the picker asks «already carded» about. */
export const receivingDeckOf = (r: ReceivingDeck | null): { id: string; title: string } | null =>
  r?.kind === 'existing' ? { id: r.id, title: r.title } : null

/** True when the cards have somewhere to go: only several links with no choice do not. */
export const deckResolved = (r: ReceivingDeck | null) => r?.kind !== 'unchosen'

/** The deck the save makes, if any: one linked through «Привязать колоду», or the cards' new deck. */
export function madeDeck(d: EditDraft): { title: string; isActive: boolean } | null {
  if (!d.newDeck?.linked && receivingDeck(d)?.kind !== 'new') return null
  return { title: newDeckTitle(d), isActive: d.newDeck?.isActive ?? true }
}

/** One row of the decks the text will be linked to once saved, in the «Колоды» tab's order. */
export type DraftLink =
  | { kind: 'existing'; id: string; title: string; receives: boolean; added: boolean }
  | { kind: 'new'; title: string; receives: boolean }

/**
 * The text's links as the save leaves them: the draft's own, the cards' deck when the
 * save links it, and the deck the save makes. The cards' deck is marked `receives`:
 * it is changed in the deck question, not unlinked.
 */
export function draftLinks(d: EditDraft): DraftLink[] {
  const r = receivingDeck(d)
  const out: DraftLink[] = d.deckIds.map((id) => ({
    kind: 'existing', id, title: d.deckTitles[id] ?? '', receives: r?.kind === 'existing' && r.id === id, added: false,
  }))
  if (r?.kind === 'existing' && !r.linked) out.push({ kind: 'existing', id: r.id, title: r.title, receives: true, added: true })
  const made = madeDeck(d)
  if (made) out.push({ kind: 'new', title: made.title, receives: r?.kind === 'new' })
  return out
}

/** The picker's card marks as the draft keeps them: one span per card the save would send, with its text. */
export function cardMarksFromMarks(body: string, analysis: TextAnalysis, marks: Marks): EditCardMark[] {
  return saveCardMarks(analysis, marks).map((m) => ({ ...m, text: body.slice(m.start, m.end) }))
}

/**
 * The draft's card marks put back on the picker's marks: a single word as its card key,
 * a run of words as a Card phrase, or as the Both role of a gap phrase over the same
 * words. A mark that no longer sits on the analysed words, or would cross another
 * phrase, is left out.
 */
export function withCardMarks(analysis: TextAnalysis, marks: Marks, cards: EditCardMark[]): Marks {
  const words = analysis.words
  const byStart = new Map(words.map((w, i) => [w.start, i]))
  const byEnd = new Map(words.map((w, i) => [w.end, i]))
  let next = marks
  cards.forEach((m, n) => {
    const a = byStart.get(m.start)
    const z = byEnd.get(m.end)
    if (a === undefined || z === undefined || z < a) return
    if (a === z) {
      const key = wordKey(words[a])
      if (key && !next.cards.includes(key)) next = { ...next, cards: [...next.cards, key] }
      return
    }
    const same = next.phrases.find((p) => p.first === a && p.last === z)
    if (same) {
      if (!hasCard(same.role)) {
        next = { ...next, phrases: next.phrases.map((p) => (p === same ? { ...p, role: 'both' } : p)) }
      }
      return
    }
    if (next.phrases.some((p) => p.first <= z && p.last >= a)) return
    next = { ...next, phrases: [...next.phrases, { id: `c${n}`, first: a, last: z, role: 'card' }] }
  })
  return next
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i])
const sameSet = (a: string[], b: string[]) => a.length === b.length && new Set(a).size === new Set([...a, ...b]).size

/** Whether leaving would lose anything. A form edit the learner undid counts as no change. */
export function isDirty(text: TextDetail, d: EditDraft): boolean {
  return (
    d.title.trim() !== text.title ||
    d.body !== text.body ||
    !sameSet(d.forms, text.gaps.map((g) => g.form)) ||
    !sameList(d.deckIds, text.deck_ids) ||
    d.dropWaiting.length > 0 ||
    Object.keys(d.labelEdits).length > 0 ||
    Object.values(d.alternativeEdits).some((e) => e.add.length > 0 || e.remove.length > 0) ||
    // An edit that only marks cards is an edit, and saves (Amendment 3); so is a new deck.
    d.cardMarks.length > 0 ||
    madeDeck(d) !== null
  )
}

/** One line of the save sheet (§15.5). The page words it; this says what and about which form. */
export type Change =
  | { kind: 'title'; title: string }
  | { kind: 'body' }
  | { kind: 'gapRemoved' | 'gapDropped' | 'gapNew'; form: string }
  | { kind: 'places'; form: string; count: number }
  | { kind: 'deckLinked' | 'deckUnlinked' | 'deckNew'; title: string }
  | { kind: 'answerAdded' | 'answerRemoved'; form: string; answer: string }
  | { kind: 'label'; form: string; label: string }
  | { kind: 'labelHidden' | 'labelReset'; form: string }
  | { kind: 'waiting'; word: string }

/**
 * The changes the draft makes, given the latest preview of it. Edits to a gap the
 * save removes are not listed (the save leaves them out too, see `buildPayload`).
 */
export function listChanges(text: TextDetail, d: EditDraft, preview: TextPreviewGap[]): Change[] {
  const out: Change[] = []
  if (d.title.trim() !== text.title) out.push({ kind: 'title', title: d.title.trim() })
  if (d.body !== text.body) out.push({ kind: 'body' })
  const saved = new Map(text.gaps.map((g) => [g.form, g]))
  const wanted = new Set(d.forms)
  const surviving = new Set<string>()
  for (const g of preview) {
    if (g.status === 'removed') {
      if (saved.has(g.form)) out.push({ kind: wanted.has(g.form) ? 'gapRemoved' : 'gapDropped', form: g.form })
      continue
    }
    surviving.add(g.form)
    const was = saved.get(g.form)
    if (!was) out.push({ kind: 'gapNew', form: g.form })
    else if (g.occurrence_count !== was.occurrences.length) out.push({ kind: 'places', form: g.form, count: g.occurrence_count })
  }
  for (const id of text.deck_ids) if (!d.deckIds.includes(id)) out.push({ kind: 'deckUnlinked', title: d.deckTitles[id] ?? '' })
  for (const id of d.deckIds) if (!text.deck_ids.includes(id)) out.push({ kind: 'deckLinked', title: d.deckTitles[id] ?? '' })
  const r = receivingDeck(d)
  if (r?.kind === 'existing' && !r.linked && !text.deck_ids.includes(r.id)) out.push({ kind: 'deckLinked', title: r.title })
  const made = madeDeck(d)
  if (made) out.push({ kind: 'deckNew', title: made.title })
  for (const [form, edit] of Object.entries(d.alternativeEdits)) {
    if (!surviving.has(form)) continue
    const gap = saved.get(form)
    for (const id of edit.remove) {
      const answer = gap?.alternatives.find((a) => a.id === id)?.answer
      if (answer) out.push({ kind: 'answerRemoved', form, answer })
    }
    for (const answer of edit.add) out.push({ kind: 'answerAdded', form, answer })
  }
  for (const [form, edit] of Object.entries(d.labelEdits)) {
    if (!surviving.has(form)) continue
    if (edit.label !== undefined) out.push({ kind: 'label', form, label: edit.label })
    else out.push({ kind: edit.hide ? 'labelHidden' : 'labelReset', form })
  }
  for (const id of d.dropWaiting) {
    const word = text.waiting_words.find((w) => w.id === id)?.word
    if (word) out.push({ kind: 'waiting', word })
  }
  return out
}

/**
 * The one PATCH that saves everything. Only what changed is sent. Label and answer
 * edits for a gap this same save removes are left out: the server refuses an edit to a
 * gap that is going (and it would be lost anyway).
 */
export function buildPayload(text: TextDetail, d: EditDraft, preview: TextPreviewGap[]): TextUpdatePayload {
  const payload: TextUpdatePayload = {}
  if (d.title.trim() !== text.title) payload.title = d.title.trim()
  if (d.body !== text.body) payload.body = d.body
  if (!sameSet(d.forms, text.gaps.map((g) => g.form))) payload.forms = d.forms
  if (!sameList(d.deckIds, text.deck_ids)) payload.deck_ids = d.deckIds
  if (d.dropWaiting.length > 0) payload.drop_waiting_ids = d.dropWaiting
  const surviving = new Set(preview.filter(isLive).map((g) => g.form))
  const labels = Object.entries(d.labelEdits).filter(([form]) => surviving.has(form))
  if (labels.length > 0) payload.label_corrections = Object.fromEntries(labels)
  const answers = Object.entries(d.alternativeEdits).filter(
    ([form, e]) => surviving.has(form) && (e.add.length > 0 || e.remove.length > 0),
  )
  if (answers.length > 0) payload.alternatives = Object.fromEntries(answers)
  const made = madeDeck(d)
  if (made) payload.new_deck = { title: made.title, is_active_for_learning: made.isActive }
  const r = receivingDeck(d)
  if (r) {
    payload.card_marks = d.cardMarks.map(({ start, end }) => ({ start, end }))
    // Always sent: the server never infers the deck from the links (Amendment 4).
    if (r.kind === 'existing') payload.card_destination = { kind: 'existing_deck', deck_id: r.id }
    else if (r.kind === 'new') payload.card_destination = { kind: 'new_deck' }
  }
  return payload
}

/**
 * The word picker's gap marks for a text's current gaps: each gap form's first
 * occurrence (the server's, from the preview) mapped onto the analysed words, a single
 * word as a word gap and a run of words as a phrase. A gap whose occurrence does not
 * sit on word boundaries cannot be drawn there; its form is returned in `carried` and
 * rides through the picker unchanged.
 */
export function marksFromGaps(analysis: TextAnalysis, gaps: TextPreviewGap[]): { marks: Marks; carried: string[] } {
  const words = analysis.words
  const byStart = new Map(words.map((w, i) => [w.start, i]))
  const byEnd = new Map(words.map((w, i) => [w.end, i]))
  const wordGaps: WordGap[] = []
  const phrases: Phrase[] = []
  const carried: string[] = []
  gaps.filter(isLive).forEach((g, n) => {
    const first = g.occurrences[0]
    const a = first ? byStart.get(first[0]) : undefined
    const z = first ? byEnd.get(first[1]) : undefined
    if (a === undefined || z === undefined || z < a) carried.push(g.form)
    else if (a === z) wordGaps.push({ form: words[a].text, word: a })
    else phrases.push({ id: `e${n}`, first: a, last: z, role: 'gap' })
  })
  return { marks: { ...EMPTY_MARKS, wordGaps, phrases }, carried }
}
